/**
 * The single API client instance for SonAuto Admin, plus the TanStack Query
 * client it feeds.
 *
 * Everything the app knows about the server goes through `api`. No screen calls
 * `fetch`: the wrapper is where the refresh lock, the Idempotency-Key and the
 * typed error mapping live.
 */
import { QueryClient } from '@tanstack/react-query';
import { ApiError, createApi, createApiClient, isApiError } from '@sonauto/api-client';
import type { RequestSettledInfo } from '@sonauto/api-client';
import { clearTokens, getTokens, setTokens } from './tokenStorage.js';

/**
 * Defaults to `/api`: in dev Vite proxies it to the Worker on 8787 (see
 * vite.config.ts). When the app is served from a DIFFERENT origin than the
 * API (Cloudflare Pages + a *.workers.dev Worker), build with
 * `VITE_API_BASE_URL=https://<worker-host>/api` — the same convention the POS
 * and Staff apps already follow, and the realtime WebSocket URL derives from
 * this value too.
 */
const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api';

/** Set when the client gives up on the session; the AuthProvider subscribes. */
type UnauthenticatedListener = () => void;
const unauthenticatedListeners = new Set<UnauthenticatedListener>();

export function onUnauthenticated(listener: UnauthenticatedListener): () => void {
  unauthenticatedListeners.add(listener);
  return () => unauthenticatedListeners.delete(listener);
}

/**
 * Last successful round trip, used by `useOnlineStatus` to distinguish "the OS
 * says we have wifi" from "the API is actually answering".
 */
let lastSuccessAt: number | null = null;
export function getLastSuccessAt(): number | null {
  return lastSuccessAt;
}

const client = createApiClient({
  baseUrl: BASE_URL,
  getTokens,
  setTokens,
  onUnauthenticated: () => {
    clearTokens();
    for (const listener of unauthenticatedListeners) listener();
  },
  timeoutMs: 20_000,
  clientVersion: `admin/${__APP_VERSION__}`,
  onRequestSettled: (info: RequestSettledInfo) => {
    if (info.error === null) lastSuccessAt = Date.now();
  },
});

export const api = createApi(client);

/**
 * The RAW client, for the admin-only endpoint slices in `src/lib/adminApi/*`.
 *
 * `@sonauto/api-client` only wraps the endpoints that existed when the three
 * PWAs were first built. The newer admin surface (inventory, HR, CRM, finance,
 * alerts, audit, settings, reports, approvals, notifications, realtime) is
 * wrapped app-locally on top of this client so every call still goes through
 * the refresh lock, idempotency keys and typed error mapping.
 */
export const apiClient = client;

/**
 * `x-action-reason` travels as an HTTP header, and browser `Headers` values are
 * Latin-1 ByteStrings — `headers.set()` THROWS on Vietnamese diacritics
 * (ế, ị, …). Reasons typed in the ConfirmDialog are Vietnamese, so any reason
 * that is not printable-ASCII is URL-encoded before it is put on the wire.
 * ASCII reasons pass through untouched so audit rows stay readable.
 */
export function headerReason(reason: string): string {
  const trimmed = reason.trim();
  // eslint-disable-next-line no-control-regex
  return /^[\x20-\x7e]*$/.test(trimmed) ? trimmed : encodeURIComponent(trimmed);
}

/** Vietnamese message for any thrown value. Screens show this verbatim. */
export function errorMessage(error: unknown): string {
  if (isApiError(error)) return error.message;
  if (error instanceof Error) return error.message;
  return 'Lỗi không xác định.';
}

export function errorCode(error: unknown): string | null {
  return isApiError(error) ? error.code : null;
}

export function errorRequestId(error: unknown): string | null {
  return isApiError(error) ? error.requestId : null;
}

export { ApiError, isApiError };

/**
 * Query defaults.
 *
 * - `retry` deliberately does NOT retry auth/permission/validation errors:
 *   hammering a 403 three times just delays the error the user needs to see.
 * - `staleTime` of 30s stops a tab switch from re-fetching every list, while
 *   still being short enough that a colleague's edit shows up quickly.
 * - Mutations never retry automatically. The API is idempotency-keyed, but an
 *   automatic retry of "hủy đơn" is a decision only a human should make.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          if (error.isAuthError || error.isPermissionError) return false;
          if (error.code === 'VALIDATION_FAILED' || error.code === 'NOT_FOUND') return false;
          if (error.code === 'OFFLINE') return false;
        }
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});
